import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import ConfirmModal from '../../common/modal/ConfirmModal';
import AlertModal from '../../common/modal/AlertModal';
import {
  createSmsTemplate,
  deleteSmsTemplate,
  getSmsTemplateDetail,
  updateSmsTemplate,
} from '../../../api/sms';

import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseIconButton,
  ModalContent,
  ContentRow,
  SettingsSection,
  AutoSendRow,
  AutoSendLabel,
  CustomSwitch,
  FieldGroup,
  FieldLabel,
  FieldTitleRow,
  StyledInput,
  StyledTextArea,
  ByteCountText,
  AdCheckRow,
  DataLabelRow,
  HelpIcon,
  VariableDataBox,
  VariableTag,
  PreviewSection,
  PreviewTitle,
  PreviewContent,
  PreviewPlaceholder,
  ModalFooter,
  CancelButton,
  SaveButton,
  DeleteButton,
} from './SmsTemplateEditStyled';

const VARIABLE_TAGS = [
  { key: 'memberName', label: '[회원명]' },
  { key: 'ticketName', label: '[회원권 명]' },
  { key: 'companyName', label: '[업체명]' },
  { key: 'branchName', label: '[지점명]' },
  { key: 'paymentAmount', label: '[결제금액]' },
  { key: 'paymentDate', label: '[결제일시]' },
];

const AD_PREFIX = '(광고)';
const AD_SUFFIX = '\n무료수신거부 080-0000-0000';

const getByteLength = str => {
  let byte = 0;
  for (let i = 0; i < str.length; i++) {
    byte += str.charCodeAt(i) > 127 ? 2 : 1;
  }
  return byte;
};

const SmsTemplateEdit = ({ open, onClose, templateData = null }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useSelector(state => state.user);
  const branchId = user?.branch?.branchId;
  const isEditMode = !!templateData;

  const [autoSend, setAutoSend] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [isAdText, setIsAdText] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const createMutation = useMutation({
    mutationFn: data => createSmsTemplate(branchId, data, user.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smsTemplates', branchId] });
      setShowSaveSuccess(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, data }) => updateSmsTemplate(branchId, templateId, data, user.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smsTemplates', branchId] });
      queryClient.invalidateQueries({ queryKey: ['smsTemplateDetail', branchId, templateData?.id] });
      setShowSaveSuccess(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: templateId => deleteSmsTemplate(branchId, templateId, user.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smsTemplates', branchId] });
      setShowDeleteConfirm(false);
      setShowDeleteSuccess(true);
    },
  });

  // 수정 모드일 때 상세 조회
  const { data: templateDetail } = useQuery({
    queryKey: ['smsTemplateDetail', branchId, templateData?.id],
    queryFn: () => getSmsTemplateDetail(branchId, templateData.id, user.token),
    enabled: open && !!branchId && !!user?.token && !!templateData?.id,
  });

  useEffect(() => {
    if (open && templateDetail) {
      setTemplateName(templateDetail.templateName || '');
      setTemplateContent(templateDetail.content || '');
      setAutoSend(templateDetail.autoSend || false);
      setIsAdText(templateDetail.isAdText || false);
    } else if (open && !templateData) {
      setTemplateName('');
      setTemplateContent('');
      setAutoSend(false);
      setIsAdText(false);
    }
  }, [open, templateData, templateDetail]);

  const getPreviewContent = useCallback(() => {
    if (!templateContent) return '';
    let content = templateContent;
    if (isAdText) {
      content = `${AD_PREFIX} ${content}${AD_SUFFIX}`;
    }
    return content;
  }, [templateContent, isAdText]);

  const getFullContent = useCallback(() => {
    let content = templateContent;
    if (isAdText) {
      content = `${AD_PREFIX} ${content}${AD_SUFFIX}`;
    }
    return content;
  }, [templateContent, isAdText]);

  const byteLength = getByteLength(getFullContent());
  const messageType = byteLength > 2000 ? 'MMS' : byteLength > 90 ? 'LMS' : 'SMS';

  const handleVariableClick = tag => {
    setTemplateContent(prev => prev + tag.label);
  };

  const handleSave = () => {
    if (!branchId) {
      toast.error(t('지점 정보가 없습니다'));
      return;
    }
    if (!templateName.trim()) {
      toast.error(t('템플릿 명을 입력해주세요'));
      return;
    }
    if (!templateContent.trim()) {
      toast.error(t('템플릿 내용을 입력해주세요'));
      return;
    }

    if (isEditMode) {
      updateMutation.mutate({
        templateId: templateData.id,
        data: {
          templateName,
          content: getFullContent(),
          useYn: templateDetail?.useYn || 'Y',
        },
      });
      return;
    }

    createMutation.mutate({
      templateName,
      content: getFullContent(),
    });
  };

  const handleSaveSuccessClose = () => {
    setShowSaveSuccess(false);
    handleClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (!templateData?.id) return;
    deleteMutation.mutate(templateData.id);
  };

  const handleDeleteSuccessClose = () => {
    setShowDeleteSuccess(false);
    handleClose();
  };

  const handleClose = () => {
    setTemplateName('');
    setTemplateContent('');
    setAutoSend(false);
    setIsAdText(false);
    setShowDeleteConfirm(false);
    setShowDeleteSuccess(false);
    setShowSaveSuccess(false);
    onClose();
  };

  return (
    <>
    <StyledModal open={open} onClose={handleClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{isEditMode ? t('템플릿 수정') : t('템플릿 만들기')}</ModalTitle>
          <CloseIconButton onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 28 }} />
          </CloseIconButton>
        </ModalHeader>

        <ModalContent>
          <ContentRow>
            {/* 왼쪽: 템플릿 설정 */}
            <SettingsSection>
              {/* 자동 발송 설정 */}
              <AutoSendRow>
                <AutoSendLabel>{t('자동 발송 설정')}</AutoSendLabel>
                <CustomSwitch checked={autoSend} onChange={e => setAutoSend(e.target.checked)} />
              </AutoSendRow>

              {/* 템플릿 명 */}
              <FieldGroup>
                <FieldLabel>{t('템플릿 명')}</FieldLabel>
                <StyledInput
                  placeholder={t('제목을 입력하세요')}
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  size="small"
                />
              </FieldGroup>

              {/* 템플릿 내용 */}
              <FieldGroup>
                <FieldTitleRow>
                  <FieldLabel>{t('템플릿 내용')}</FieldLabel>
                  <AdCheckRow>
                    <Checkbox
                      checked={isAdText}
                      onChange={e => setIsAdText(e.target.checked)}
                      sx={{
                        padding: 0,
                        width: 20,
                        height: 20,
                        color: '#d9d9d9',
                        '&.Mui-checked': { color: '#4ab3bc' },
                      }}
                    />
                    <FieldLabel>{t('광고성 문자')}</FieldLabel>
                  </AdCheckRow>
                </FieldTitleRow>
                <StyledTextArea
                  placeholder={t('문자 내용을 여기에 입력해주세요.')}
                  value={templateContent}
                  onChange={e => setTemplateContent(e.target.value)}
                  multiline
                  rows={6}
                />
                <ByteCountText>{`${byteLength} byte /  ${messageType}`}</ByteCountText>
              </FieldGroup>

              {/* 변경 데이터 선택 */}
              <FieldGroup>
                <DataLabelRow>
                  <FieldLabel>{t('변경 데이터 선택')}</FieldLabel>
                  <Tooltip title={t('클릭하면 템플릿 내용에 자동으로 삽입됩니다')} arrow>
                    <HelpIcon>
                      <HelpOutlineIcon sx={{ fontSize: 20, color: '#a6a6a6' }} />
                    </HelpIcon>
                  </Tooltip>
                </DataLabelRow>
                <VariableDataBox>
                  {VARIABLE_TAGS.map(tag => (
                    <VariableTag key={tag.key} onClick={() => handleVariableClick(tag)}>
                      {tag.label}
                    </VariableTag>
                  ))}
                </VariableDataBox>
              </FieldGroup>
            </SettingsSection>

            {/* 오른쪽: 미리보기 */}
            <PreviewSection>
              <PreviewTitle>{t('미리보기')}</PreviewTitle>
              <PreviewContent>
                {getPreviewContent() ? getPreviewContent() : <PreviewPlaceholder>{t('내용을 작성중입니다.')}</PreviewPlaceholder>}
              </PreviewContent>
            </PreviewSection>
          </ContentRow>

          <ModalFooter>
            {isEditMode && (
              <DeleteButton onClick={handleDelete} disabled={deleteMutation.isPending}>
                {t('삭제')}
              </DeleteButton>
            )}
            <CancelButton onClick={handleClose}>{t('취소')}</CancelButton>
            <SaveButton
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}>
              {t('저장')}
            </SaveButton>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </StyledModal>

    <ConfirmModal
      open={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteConfirm}
      highlightText="삭제"
      highlightColor="#F44336"
      cancelText="취소"
      confirmText="삭제"
      confirmColor="#F44336"
    />

    <AlertModal
      open={showDeleteSuccess}
      onClose={handleDeleteSuccessClose}
      message="삭제가 완료되었습니다."
      buttonText="확인"
      buttonColor="#0F7F85"
    />

    <AlertModal
      open={showSaveSuccess}
      onClose={handleSaveSuccessClose}
      message="저장이 완료되었습니다."
      buttonText="확인"
      buttonColor="#0F7F85"
    />
  </>
  );
};

export default SmsTemplateEdit;
